import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { Subject, filter, takeUntil } from 'rxjs';
import { EntityDefinition } from '../../../../../types/entity-definition.interface';
import { AutomaticMeasureFollow } from '../../../../../types/measure.interface';
import { AssetMeasureTypeOptionsService } from '../../../../../services/asset-measure-type-options.service';
import { UsedLocationOptionsService } from '../../../../../services/used-location-options.service';
import { buildLocationFilterColumn } from '../../../../../utils/location-filter';
import { ParsedFilters, PaginatedUrlSyncService } from '../../../../../services/paginated-url-sync.service';
import { LocationStore } from '../../../../../stores/location.store';
import { MeasureTypeStore } from '../../../../../stores/measure-type.store';
import { AutomaticMeasureFollowUpStore } from '../../../../../stores/automatic-measure-follow.store';
import { LazyLoadConverter } from '../../../../../utils/lazy-load.converter';
import { automaticMeasureFollowUpDefinition } from './automatic-measure-follow-up.definition';
import { GenericParentGridComponent } from '../../../../generic/generic-parent-grid.component';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';

@Component({
  selector: 'app-measure-automatic-follow-up-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesAssetBreadcrumbComponent, CesTitleComponent, GenericParentGridComponent, TranslocoModule],
  templateUrl: './measure-automatic-follow-up-list.component.html',
})
export class MeasureAutomaticFollowUpListComponent implements OnInit, OnDestroy {
  private readonly store = inject(AutomaticMeasureFollowUpStore);
  private readonly measureTypeStore = inject(MeasureTypeStore);
  private readonly measureTypeOptions = inject(AssetMeasureTypeOptionsService);
  private readonly usedLocations = inject(UsedLocationOptionsService);
  private readonly locationStore = inject(LocationStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly paginatedUrlSync = inject(PaginatedUrlSyncService);
  private readonly translations = toSignal(this.translate.selectTranslation());

  private readonly destroy$ = new Subject<void>();
  private isLoadingFromGrid = false;
  private isInitialLoad = true;

  readonly currentAssetId = signal<number | null>(null);
  readonly dataLoadingCompleted = signal(false);
  private readonly measureTypeNames = signal<Map<number, string>>(new Map());
  private readonly locationNames = signal<Map<number, string>>(new Map());

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return [{ label: this.translate.translate('navigation.routes.automaticMeasurements') }];
  });

  readonly entityDefinition = computed<EntityDefinition<AutomaticMeasureFollow>>(() => {
    this.translations();
    const def = automaticMeasureFollowUpDefinition;
    return {
      ...def,
      list: {
        columns: def.list.columns.map(col => {
          if (col.field === 'measureTypeName') return { ...col, options: this.measureTypeOptions.options() };
          if (col.field === 'localisationName')
            return buildLocationFilterColumn<AutomaticMeasureFollow>(this.usedLocations.options('automatic')(), 'server');
          return col;
        }),
      },
      rowLinkHref: measure => ['/assets', measure.assetId, 'monitoring', 'automatic-measurements', measure.id],
    };
  });

  readonly urlFilters = signal<ParsedFilters | undefined>(
    this.paginatedUrlSync.parseFiltersFromUrl(this.route.snapshot.queryParams, this.entityDefinition()),
  );

  readonly entities = computed<AutomaticMeasureFollow[]>(() => {
    const assetId = this.currentAssetId();
    if (!assetId) return [];
    const raw = this.store.measuresByAssetId(assetId);
    const typeMap = this.measureTypeNames();
    const locMap = this.locationNames();
    return raw.map(m => ({
      ...m,
      measureTypeName: typeMap.get(m.measureTypeId) ?? '',
      localisationName: this.normalizeLocationsIds(m.locationsIds)
        .map(id => locMap.get(id) ?? '')
        .filter(Boolean)
        .join(', '),
      lastValueDate: m.lastValueDate ? new Date(m.lastValueDate) : null,
    }));
  });

  private normalizeLocationsIds(raw: number[] | string | null | undefined): number[] {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      return raw
        .split(',')
        .map(part => Number(part.trim()))
        .filter(n => !isNaN(n));
    }
    return [];
  }

  readonly totalRecords = computed(() => this.store.paginationState().totalItems);
  readonly first = computed(() => {
    const page = this.store.paginationState().currentPage;
    const limit = this.store.paginationState().limit;
    return (page - 1) * limit;
  });
  readonly rows = computed(() => this.store.paginationState().limit);

  ngOnInit(): void {
    this.route.parent?.parent?.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = params['id'] ? Number(params['id']) : null;
      this.currentAssetId.set(id && id > 0 ? id : null);
      if (id && id > 0) {
        this.measureTypeOptions.load(id);
        this.usedLocations.load(id, 'automatic');
        void this.preloadFacets(id);
        void this.loadFromUrl(id);
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(event => /\/monitoring\/automatic-measurements(\?|$)/.test(event.url)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.isLoadingFromGrid) return;
        const assetId = this.currentAssetId();
        if (assetId) void this.loadFromUrl(assetId);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    const assetId = this.currentAssetId();
    if (!assetId) return;

    if (this.isInitialLoad) {
      this.isInitialLoad = false;
      return;
    }

    this.isLoadingFromGrid = true;
    try {
      const queryParams = this.paginatedUrlSync.buildQueryParamsFromEvent(event, this.entityDefinition());
      this.paginatedUrlSync.saveState(this.paginatedUrlSync.buildStateKey('automatic-measure-follow-up-grid', assetId), queryParams);
      await this.paginatedUrlSync.navigateWithParams(this.router, this.route, queryParams);
      const params = LazyLoadConverter.fromTableEvent(event);
      await this.store.loadPaginatedByAssetId(assetId, params);
      this.dataLoadingCompleted.set(true);
    } finally {
      this.isLoadingFromGrid = false;
    }
  }

  private async preloadFacets(assetId: number): Promise<void> {
    const [types, locations] = await Promise.all([
      this.measureTypeStore.getByAssetId(assetId),
      this.locationStore.getByAssetId(assetId),
    ]);
    this.measureTypeNames.set(new Map(types.map(t => [t.id, t.name])));
    this.locationNames.set(new Map(locations.map(l => [l.id, l.name])));
  }

  private async loadFromUrl(assetId: number): Promise<void> {
    const { queryParams, toSync } = this.paginatedUrlSync.resolveState(
      this.route,
      this.paginatedUrlSync.buildStateKey('automatic-measure-follow-up-grid', assetId),
    );
    const tableEvent = this.paginatedUrlSync.buildTableEventFromUrl(queryParams, this.entityDefinition());
    this.urlFilters.set(tableEvent.filters);

    if (toSync) {
      this.isLoadingFromGrid = true;
      this.paginatedUrlSync.syncUrlInBackground(this.router, this.route, toSync, () => (this.isLoadingFromGrid = false));
    }

    this.dataLoadingCompleted.set(false);
    try {
      const params = LazyLoadConverter.fromTableEvent(tableEvent);
      await this.store.loadPaginatedByAssetId(assetId, params);
    } finally {
      this.dataLoadingCompleted.set(true);
    }
  }
}
